import { useState, useEffect } from 'react';
import {
  Dialog,
  Flex,
  Button,
  Text,
  Tooltip,
  ScrollArea,
} from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import RadixSelect from '../../../shared/RadixSelect/RadixSelect.jsx';
import CreatableSelect from '../../../shared/CreatableSelect/CreatableSelect.jsx';

const MB_SIZE = 1024 * 1024;

const formatFileSize = size => {
  if (size < MB_SIZE) {
    return `${(size / 1024).toFixed(1)} KB`;
  }
  return `${(size / MB_SIZE).toFixed(1)} MB`;
};

export const UploadCategoryModal = ({
  open,
  onOpenChange,
  selectedFiles = [],
  onRemoveFile,
  onAddMoreFiles,
  onUpload,
  categories = [],
  vendors = [],
  defaultCategory,
}) => {
  const { t } = useTranslation('vault');

  const defaultCategoryOption = defaultCategory
    ? categories.find(
        cat => String(cat.value) === String(defaultCategory.value),
      ) || null
    : null;

  const [selectedCategory, setSelectedCategory] = useState(
    defaultCategoryOption,
  );
  const [selectedVendors, setSelectedVendors] = useState([]);

  useEffect(() => {
    if (open) {
      setSelectedCategory(defaultCategoryOption);
    }
  }, [open, defaultCategoryOption]);

  const categoryOptions = categories.map(cat => ({
    value: cat.value,
    label: cat.label,
  }));

  const handleUpload = () => {
    const vendorNames = selectedVendors.map(v => v.label);
    onUpload(selectedCategory || null, vendorNames);
    setSelectedCategory(null);
    setSelectedVendors([]);
  };

  const handleCancel = () => {
    setSelectedCategory(null);
    setSelectedVendors([]);
    onOpenChange(false);
  };

  const hasFiles = selectedFiles.length > 0;
  const canUpload = hasFiles && selectedCategory;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          setSelectedCategory(null);
          setSelectedVendors([]);
        }
        onOpenChange(isOpen);
      }}
    >
      <Dialog.Content maxWidth="500px" style={{ overflow: 'visible' }}>
        <Dialog.Title mt="0" mb="4" size="4">
          {t('upload')}
        </Dialog.Title>

        <Flex direction="column" gap="3">
          <Text size="2" color="gray" weight="medium">
            {t('selectedFilesLabel', { count: selectedFiles.length })}
          </Text>

          {hasFiles ? (
            <ScrollArea style={{ maxHeight: '200px' }} scrollbars="vertical">
              <Flex direction="column" gap="2">
                {selectedFiles.map((file, index) => (
                  <Flex
                    key={`${file.name}-${index}`}
                    align="center"
                    justify="between"
                    gap="2"
                    p="2"
                    style={{
                      backgroundColor: 'var(--gray-2)',
                      borderRadius: '6px',
                    }}
                  >
                    <Flex
                      align="center"
                      gap="2"
                      style={{ minWidth: 0, flex: 1 }}
                    >
                      <i
                        className="fa-solid fa-file"
                        style={{
                          color: 'var(--gray-9)',
                          fontSize: '14px',
                          flexShrink: 0,
                        }}
                      />
                      <Text
                        size="2"
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {file.name}
                      </Text>
                      <Text size="1" color="gray" style={{ flexShrink: 0 }}>
                        {formatFileSize(file.size)}
                      </Text>
                    </Flex>
                    <Tooltip content={t('removeFile')}>
                      <i
                        className="fa-solid fa-xmark"
                        role="button"
                        tabIndex={0}
                        onClick={() => onRemoveFile(index)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ')
                            onRemoveFile(index);
                        }}
                        style={{
                          color: 'var(--gray-9)',
                          fontSize: '12px',
                          cursor: 'pointer',
                          padding: '4px',
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                  </Flex>
                ))}
              </Flex>
            </ScrollArea>
          ) : (
            <Flex
              align="center"
              justify="center"
              direction="column"
              gap="2"
              p="4"
              style={{
                backgroundColor: 'var(--gray-2)',
                borderRadius: '8px',
                border: '1px dashed var(--gray-6)',
              }}
            >
              <Text size="2" color="gray">
                {t('noFilesSelected')}
              </Text>
            </Flex>
          )}

          <Button variant="soft" size="2" onClick={onAddMoreFiles}>
            <i className="fa-solid fa-plus" />
            {hasFiles ? t('addMoreFiles') : t('chooseFiles')}
          </Button>

          <Dialog.Description size="2" mt="1" mb="0">
            {t('selectCategoryAndVendor')}
          </Dialog.Description>

          <RadixSelect
            options={categoryOptions}
            value={selectedCategory}
            onChange={setSelectedCategory}
            placeholder={t('selectCategory')}
            components={{ IndicatorSeparator: () => null }}
            styles={{
              control: provided => ({
                ...provided,
                borderRadius: '8px',
                borderColor: '#DBDDE0',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                minHeight: '44px',
              }),
              valueContainer: provided => ({
                ...provided,
                padding: '10px 16px',
              }),
              placeholder: provided => ({
                ...provided,
                color: '#CACCCF',
                fontWeight: 500,
              }),
              option: provided => ({
                ...provided,
                fontSize: '14px',
                fontWeight: 500,
              }),
            }}
          />
          <CreatableSelect
            isMulti={true}
            isClearable={true}
            isSearchable={true}
            placeholder={t('selectVendor')}
            value={selectedVendors}
            onChange={selected => setSelectedVendors(selected || [])}
            options={vendors}
          />
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Button variant="outline" onClick={handleCancel}>
            {t('cancel')}
          </Button>
          {canUpload ? (
            <Button variant="solid" onClick={handleUpload}>
              <i className="fa-solid fa-cloud-arrow-up" />
              {t('uploadFiles')}
            </Button>
          ) : (
            <Tooltip
              content={
                !hasFiles ? t('chooseFilesFirst') : t('chooseCategoryFirst')
              }
            >
              <span style={{ cursor: 'not-allowed' }}>
                <Button
                  variant="solid"
                  disabled
                  style={{ pointerEvents: 'none' }}
                >
                  <i className="fa-solid fa-cloud-arrow-up" />
                  {t('uploadFiles')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};
